import { Tool } from "ai";
import { useMemo } from "react";
import * as z from "zod";

import { TOOL_CALL_DESCRIPTION_FIELD } from "components/AiChat/constants";
import { Eye } from "components/icons/generated";
import { decodeFilter } from "components/Filters/helpers";

const pageContextInputSchema = z.object({
  [TOOL_CALL_DESCRIPTION_FIELD]: z
    .string()
    .describe(
      "A short, user-friendly description of the action. Examples: 'Checking what page is open', 'Reading the current page'"
    ),
});

const decodeFilterValues = (values: string | null): unknown[] => {
  try {
    return (values && values !== "null" && JSON.parse(decodeURIComponent(atob(values)))) || [];
  } catch {
    return [];
  }
};

const parseSearchParams = (search: string) => {
  const params = new URLSearchParams(search);

  const sort = params.get("sort");
  const sortDirection = params.get("sortDirection");
  const sortInfo = sort ? { field: sort, direction: sortDirection ?? "ASC" } : undefined;

  const keys = decodeFilter(params.get("filterKeys"));
  const types = decodeFilter(params.get("filterTypes"));
  const values = decodeFilterValues(params.get("filterValues"));

  const filters = keys.map((key, i) => ({
    key,
    type: types[i],
    values: values[i],
  }));

  return {
    ...(sortInfo && { sort: sortInfo }),
    ...(filters.length > 0 && { filters }),
  };
};

const parsePageContext = () => {
  const { pathname, search } = window.location;
  const segments = pathname.split("/").filter(Boolean);
  const section = segments[0] || undefined;
  const entityId = segments[1] || undefined;
  const title = document.title || undefined;

  return {
    currentPage: pathname,
    section,
    entityId,
    title,
    ...parseSearchParams(search),
  };
};

type PageContextOutput = ReturnType<typeof parsePageContext>;

export const toolIcon = Eye;

const usePageContextTool = (): Tool<z.infer<typeof pageContextInputSchema>, PageContextOutput> =>
  useMemo(
    () => ({
      title: "Reading current page",
      description:
        "Get the current page context including URL, section, entity ID, page title, and any active sort/filter settings. Use this to understand what the user is currently looking at.",
      inputSchema: pageContextInputSchema,
      execute: async () => parsePageContext(),
    }),
    []
  );

export default usePageContextTool;
